import { Button } from "@/components/atoms/Button";
import MinusIcon from "@/icons/__generated__/Minus/Minus16";
import PlusIcon from "@/icons/__generated__/Plus/Plus16";
import { cn } from "@/lib/utils";
import * as React from "react";
import { Input, InputProps } from "../../atoms/Input";

export type CounterInputProps = Omit<InputProps, "value" | "onChange"> & {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  testId?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  allowFloat?: boolean;
};

export const CounterInput = React.forwardRef<HTMLDivElement, CounterInputProps>(
  (
    {
      testId = "counter-input",
      className,
      disabled,
      readOnly,
      value,
      onValueChange,
      fullWidth,
      allowFloat = false,
      ...inputProps
    },
    ref,
  ) => {
    const min =
      (allowFloat ? inputProps.min : Math.floor(inputProps.min ?? 0)) ?? 0;
    const max =
      (allowFloat ? inputProps.max : Math.floor(inputProps.max ?? Infinity)) ??
      Infinity;

    const handleChange = (value: string) => {
      let parsedValue = allowFloat ? parseFloat(value) : parseInt(value, 10);

      if (isNaN(parsedValue) || parsedValue < min) {
        parsedValue = min;
      } else if (parsedValue > max) {
        parsedValue = max;
      }
      onValueChange?.(parsedValue);
    };

    return (
      <div
        ref={ref}
        className={cn("tw-flex tw-gap-4 tw-h-24", {
          "tw-pointer-events-none": readOnly,
          "tw-w-full tw-h-32": fullWidth,
        })}
      >
        <Button
          appearance="white"
          variant="outline-1"
          className={cn("tw-h-full tw-w-24 tw-p-0", {
            "tw-w-full tw-flex-1 tw-max-w-[25%]": fullWidth,
          })}
          onClick={() => handleChange(String((value ?? 0) - 1))}
          disabled={disabled || value === min}
          testId={testId ? `${testId}-decrement` : undefined}
        >
          <MinusIcon />
        </Button>
        <Input
          className={cn("tw-text-center tw-py-0 tw-px-2 tw-h-full tw-w-40", {
            "tw-flex-1 tw-w-full": fullWidth,
          })}
          type="number"
          disabled={disabled}
          readOnly={readOnly}
          fullWidth={fullWidth}
          {...inputProps}
          value={String(value)}
          onChange={(e) => handleChange(e.target.value)}
          testId={testId}
        />
        <Button
          appearance="white"
          variant="outline-1"
          className={cn("tw-h-full tw-w-24 tw-p-0", {
            "tw-w-full tw-flex-1 tw-max-w-[25%]": fullWidth,
          })}
          onClick={() => handleChange(String((value ?? 0) + 1))}
          disabled={disabled || value === max}
          testId={testId ? `${testId}-increment` : undefined}
        >
          <PlusIcon />
        </Button>
      </div>
    );
  },
);
CounterInput.displayName = "CounterInput";
